import { useState, useEffect } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import features from "@/config/features";
import {
  Play, CheckCircle, Clock, BookOpen, ChevronRight,
  ArrowLeft, Lock, Video, Award, Home, FileText,
  HelpCircle, X, Check, Eye, Crown, Star, Trophy, Target
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CoursesPage() {
  const navigate = useNavigate();
  const { chapterId } = useParams();
  const { user } = useAuth();
  const [chapters, setChapters] = useState([]);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [chapterTheory, setChapterTheory] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [theoryLoading, setTheoryLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("theory");
  const [progressOverview, setProgressOverview] = useState(null);

  // Quiz states
  const [chapterQuiz, setChapterQuiz] = useState(null);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResults, setQuizResults] = useState(null);
  const [quizLoading, setQuizLoading] = useState(false);
  const [explanationVideo, setExplanationVideo] = useState(null);

  const currentChapterIndex = selectedChapter
    ? chapters.findIndex((c) => c.id === selectedChapter.id)
    : -1;

  const previousChapter =
    currentChapterIndex > 0 ? chapters[currentChapterIndex - 1] : null;

  const nextChapter =
    currentChapterIndex >= 0 && currentChapterIndex < chapters.length - 1
      ? chapters[currentChapterIndex + 1]
      : null;

  useEffect(() => {
    fetchChapters();
    if (user) {
      fetchProgressOverview();
    }
  }, [user]);
  useEffect(() => {
    if (!chapterId) {
      setSelectedChapter(null);
      setChapterTheory(null);
      setSelectedVideo(null);
      setShowQuiz(false);
      setChapterQuiz(null);
      setQuizAnswers({});
      setQuizResults(null);
      setExplanationVideo(null);
      return;
    }

    if (chapters.length === 0) return;

    const chapter = chapters.find(c => c.id === chapterId);
    if (chapter) {
      setSelectedChapter(chapter);
      setActiveTab("theory");
      fetchChapterTheory(chapter.id);
    }
  }, [chapterId, chapters]);

  const fetchChapters = async () => {
    try {
      const res = await axios.get(`${API}/chapters`);
      setChapters(res.data);
    } catch (error) {
      // Fallback to old endpoint if new one doesn't exist yet
      try {
        const res = await axios.get(`${API}/videos/chapters`);
        setChapters(res.data);
      } catch (e) {
        toast.error("Eroare la incarcarea cursurilor");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchProgressOverview = async () => {
    try {
      const res = await axios.get(`${API}/progress/overview`);
      setProgressOverview(res.data);
    } catch (error) {
      console.error("Error fetching progress:", error);
    }
  };

  const markTheoryComplete = async (chapterId) => {
    try {
      await axios.post(`${API}/chapters/${chapterId}/theory/complete`);
      toast.success("Teorie marcata ca citita!");
      fetchChapters();
      fetchProgressOverview();
    } catch (error) {
      console.error("Error marking theory complete:", error);
    }
  };

  const fetchChapterTheory = async (chapterId) => {
    setTheoryLoading(true);
    try {
      const res = await axios.get(`${API}/chapters/${chapterId}/theory`);
      setChapterTheory(res.data);
    } catch (error) {
      console.error("Error fetching theory:", error);
      setChapterTheory(null);
    } finally {
      setTheoryLoading(false);
    }
  };

  // Handle chapter selection
  const handleSelectChapter = (chapter) => {
    navigate(`/cursuri/${chapter.id}`);
  };

  // Handle back navigation
  const handleBack = () => {
    if (showQuiz) {
      closeQuiz();
    } else if (selectedVideo) {
      setSelectedVideo(null);
    } else if (selectedChapter) {
      setSelectedChapter(null);
      setChapterTheory(null);
      navigate("/cursuri");
    }
  };

  // Mark video as complete
  const markVideoComplete = async (chapterId, videoId) => {
    try {
      await axios.post(`${API}/videos/progress`, {
        chapter_id: chapterId,
        video_id: videoId,
        completed: true
      });
      toast.success("Video marcat ca vizionat!");
      fetchChapters();
    } catch (error) {
      toast.error("Eroare la salvare progres");
    }
  };

  // Fetch quiz for chapter
  const fetchChapterQuiz = async (chapterId) => {
    try {
      const res = await axios.get(`${API}/chapter-quizzes/${chapterId}`);
      setChapterQuiz(res.data);
      return res.data;
    } catch (error) {
      console.error("Error fetching quiz:", error);
      return null;
    }
  };

  // Start quiz for chapter
  const startChapterQuiz = async () => {
    if (!selectedChapter) return;

    if (!chapterTheory?.has_quiz_access) {
      toast.error("Ai nevoie de abonament Standard sau Premium pentru quiz-uri");
      return;
    }

    setQuizLoading(true);
    const quiz = await fetchChapterQuiz(selectedChapter.id);
    if (quiz && quiz.questions && quiz.questions.length > 0) {
      setQuizAnswers({});
      setQuizResults(null);
      setShowQuiz(true);
    } else {
      toast.info("Nu exista quiz pentru acest capitol inca");
    }
    setQuizLoading(false);
  };

  // Submit quiz answers
  const submitQuiz = async () => {
    if (!chapterQuiz) return;

    const totalQuestions = chapterQuiz.questions.length;
    const answeredQuestions = Object.keys(quizAnswers).length;

    if (answeredQuestions < totalQuestions) {
      toast.error(`Raspunde la toate intrebarile (${answeredQuestions}/${totalQuestions})`);
      return;
    }

    setQuizLoading(true);
    try {
      const res = await axios.post(`${API}/chapter-quizzes/${chapterQuiz.id}/submit`, {
        answers: quizAnswers
      });
      setQuizResults(res.data);
      if (res.data.passed) {
        toast.success(`Felicitari! Ai trecut quiz-ul cu ${res.data.score}%!`);
      } else {
        toast.error(`Nu ai trecut. Scor: ${res.data.score}%. Minim: ${chapterQuiz.passing_score || 80}%`);
      }
    } catch (error) {
      toast.error("Eroare la trimiterea quiz-ului");
    }
    setQuizLoading(false);
  };

  // Close quiz
  const closeQuiz = () => {
    setShowQuiz(false);
    setChapterQuiz(null);
    setQuizAnswers({});
    setQuizResults(null);
    setExplanationVideo(null);
  };

  // Extract YouTube ID from URL
  const getYouTubeId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // Get user plan display
  const getUserPlanBadge = () => {
    if (!user) return null;
    if (user.subscription_status !== "active") return null;

    const plan = user.subscription_plan;
    if (plan === "premium") {
      return <Badge className="bg-gradient-to-r from-yellow-500 to-amber-500 text-white"><Crown className="w-3 h-3 mr-1" /> Premium</Badge>;
    } else if (plan === "standard") {
      return <Badge className="bg-gradient-to-r from-blue-500 to-blue-600 text-white"><Star className="w-3 h-3 mr-1" /> Standard</Badge>;
    }
    return null;
  };

  // Quiz View
  if (showQuiz && chapterQuiz && selectedChapter) {
    return (
      <div className="min-h-screen bg-slate-100 py-8" data-testid="chapter-quiz">
        <div className="w-full px-4 md:max-w-3xl md:mx-auto">
          <Button variant="ghost" onClick={closeQuiz} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Inapoi la capitol
          </Button>

          <Card className="border-0 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <HelpCircle className="w-6 h-6" />
                </div>
                <div>
                  <CardTitle className="text-xl">Chestionar: {selectedChapter.title}</CardTitle>
                  <CardDescription className="text-blue-100">
                    {chapterQuiz.questions.length} intrebari - Minim {chapterQuiz.passing_score || 80}% pentru a trece
                  </CardDescription>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-4 md:p-6">
              {!quizResults ? (
                <div className="space-y-6">
                  {chapterQuiz.questions.map((q, idx) => {
                    const correctValue = q.correct;
                    const correctCount = Array.isArray(correctValue) ? correctValue.length : 1;
                    const isMultiple = correctCount > 1;

                    return (
                      <div key={idx} className="p-4 bg-slate-50 rounded-lg">
                        <p className="font-semibold text-slate-900 mb-3">
                          {idx + 1}. {q.question}
                        </p>
                        {q.image_url && (
                          <img
                            src={q.image_url}
                            alt="Imagine intrebare"
                            className="mb-4 mx-auto rounded-lg max-h-72 object-contain border bg-white p-2"
                          />
                        )}

                        <div className="space-y-2">
                          {isMultiple && (
                            <p className="text-sm text-amber-600 font-medium">
                              Selectează toate răspunsurile corecte
                            </p>
                          )}

                          {q.options.map((option, optIdx) => {
                            const isChecked = isMultiple
                              ? (quizAnswers[idx] || []).includes(optIdx)
                              : quizAnswers[idx] === optIdx;

                            return (
                              <label
                                key={optIdx}
                                className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${isChecked
                                  ? "bg-blue-100 border-2 border-blue-500"
                                  : "bg-white border-2 border-slate-200 hover:border-blue-300"
                                  }`}
                              >
                                <input
                                  type={isMultiple ? "checkbox" : "radio"}
                                  name={`question-${idx}`}
                                  checked={isChecked}
                                  onChange={() => {
                                    if (isMultiple) {
                                      setQuizAnswers(prev => {
                                        const current = prev[idx] || [];
                                        const updated = current.includes(optIdx)
                                          ? current.filter(i => i !== optIdx)
                                          : [...current, optIdx];

                                        return {
                                          ...prev,
                                          [idx]: updated
                                        };
                                      });
                                    } else {
                                      setQuizAnswers(prev => ({
                                        ...prev,
                                        [idx]: optIdx
                                      }));
                                    }
                                  }}
                                  className="w-4 h-4 text-blue-600"
                                />
                                <span className={isChecked ? "text-blue-900 font-medium" : "text-slate-700"}>
                                  {option}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}

                  <div className="flex justify-between items-center pt-4 border-t">
                    <p className="text-slate-500">
                      Raspunsuri: {Object.keys(quizAnswers).length}/{chapterQuiz.questions.length}
                    </p>
                    <Button
                      onClick={submitQuiz}
                      disabled={quizLoading}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {quizLoading ? "Se trimite..." : "Trimite Raspunsurile"}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className={`p-6 rounded-xl text-center ${quizResults.passed
                    ? 'bg-green-100 border-2 border-green-300'
                    : 'bg-red-100 border-2 border-red-300'
                    }`}>
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${quizResults.passed ? 'bg-green-500' : 'bg-red-500'
                      }`}>
                      {quizResults.passed ? (
                        <Check className="w-8 h-8 text-white" />
                      ) : (
                        <X className="w-8 h-8 text-white" />
                      )}
                    </div>
                    <h3 className={`text-2xl font-bold ${quizResults.passed ? 'text-green-800' : 'text-red-800'}`}>
                      {quizResults.passed ? 'Felicitari!' : 'Mai incearca!'}
                    </h3>
                    <p className={`text-lg mt-2 ${quizResults.passed ? 'text-green-700' : 'text-red-700'}`}>
                      Scor: {quizResults.score}% ({quizResults.correct}/{quizResults.total} corecte)
                    </p>
                  </div>

                  {/* Detailed Results - show correct answers for wrong questions */}
                  {quizResults.results && quizResults.results.length > 0 && (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      <h4 className="font-semibold text-slate-700 sticky top-0 bg-white py-2">
                        Rezultate detaliate:
                      </h4>
                      {quizResults.results.map((result, idx) => {
                        const indexToLetter = { 0: 'A', 1: 'B', 2: 'C', 3: 'D', 4: 'E', 5: 'F' };
                        const letterToIndex = { a: 0, b: 1, c: 2, d: 3, e: 4, f: 5 };
                        const question = chapterQuiz.questions[idx];

                        const correctLetters = Array.isArray(result.correct_answer)
                          ? result.correct_answer.map(a => String(a).toUpperCase())
                          : [String(result.correct_answer || '?').toUpperCase()];

                        const userLetters = Array.isArray(result.user_answer)
                          ? result.user_answer.map(a => indexToLetter[a] || '?')
                          : [indexToLetter[result.user_answer] || '?'];

                        const correctOptionText = Array.isArray(result.correct_answer)
                          ? result.correct_answer
                            .map(letter => question?.options?.[letterToIndex[String(letter).toLowerCase()]] || '')
                            .filter(Boolean)
                            .join(', ')
                          : question?.options?.[letterToIndex[String(result.correct_answer).toLowerCase()]] || '';

                        return (
                          <div
                            key={idx}
                            className={`p-3 rounded-lg border-2 ${result.is_correct
                              ? 'bg-green-50 border-green-200'
                              : 'bg-red-50 border-red-200'
                              }`}
                          >
                            <div className="flex items-start gap-2">
                              <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${result.is_correct ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                                }`}>
                                {result.is_correct ? '✓' : '✗'}
                              </span>
                              <div className="flex-1">
                                <p className="font-medium text-slate-800 text-sm">
                                  {idx + 1}. {result.question}
                                </p>
                                {result.is_correct ? (
                                  <p className="text-green-600 text-sm mt-1">
                                    Răspuns corect: {correctLetters.join(', ')} {correctOptionText ? `- ${correctOptionText}` : ''}
                                  </p>
                                ) : (
                                  <div className="mt-1 space-y-1">
                                    <p className="text-red-600 text-sm">
                                      Răspunsul tău: {userLetters.join(', ')} {Array.isArray(result.user_answer)
                                        ? `- ${result.user_answer.map(a => question?.options?.[a] || '').filter(Boolean).join(', ')}`
                                        : `- ${question?.options?.[result.user_answer] || 'N/A'}`
                                      }
                                    </p>
                                    <p className="text-green-600 text-sm font-medium">
                                      Răspuns corect: {correctLetters.join(', ')} {correctOptionText ? `- ${correctOptionText}` : ''}
                                    </p>
                                  </div>
                                )}
                                {question?.explanation_video_url && (
                                  <button
                                    onClick={() => setExplanationVideo({
                                      url: question.explanation_video_url,
                                      question: result.question
                                    })}
                                    className="mt-2 flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
                                  >
                                    <Video className="w-4 h-4" />
                                    Vezi explicație video
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="flex justify-center gap-4 pt-4 border-t">
                    <Button variant="outline" onClick={closeQuiz}>
                      Inapoi la capitol
                    </Button>
                    {!quizResults.passed && (
                      <Button onClick={() => { setQuizResults(null); setQuizAnswers({}); }}>
                        Incearca din nou
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Video Explanation Modal */}
        {explanationVideo && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setExplanationVideo(null)}>
            <div className="bg-white rounded-xl max-w-3xl w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="p-4 border-b flex justify-between items-center">
                <div>
                  <h3 className="font-semibold text-slate-900">Video Explicativ</h3>
                  <p className="text-sm text-slate-500 line-clamp-1">{explanationVideo.question}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setExplanationVideo(null)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="aspect-video bg-black">
                {getYouTubeId(explanationVideo.url) ? (
                  <iframe
                    width="100%"
                    height="100%"
                    src={`https://www.youtube.com/embed/${getYouTubeId(explanationVideo.url)}?autoplay=1&rel=0`}
                    title="Video Explicativ"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video
                    src={explanationVideo.url}
                    controls
                    autoPlay
                    className="w-full h-full"
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Chapter Detail View
  if (selectedChapter && chapterTheory) {
    return (
      <div className="min-h-screen bg-slate-100 py-8" data-testid="chapter-detail">
        <div className="w-full px-4 md:max-w-5xl md:mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <Button
              onClick={handleBack}
              className="h-12 px-5 text-base font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-md"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Inapoi la cursuri
            </Button>
            {getUserPlanBadge()}
          </div>

          {/* Chapter Header */}
          <Card className="border-0 shadow-lg mb-6 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-white">
              <div className="flex items-start gap-4">
                {chapterTheory.image_url && (
                  <img
                    src={chapterTheory.image_url}
                    alt={chapterTheory.title}
                    className="w-20 h-20 rounded-lg object-cover border-2 border-white/30"
                  />
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-white/20 text-white">Capitol {chapterTheory.order}</Badge>
                  </div>
                  <h1 className="text-2xl font-bold mb-2">{chapterTheory.title}</h1>
                  <p className="text-blue-100">{chapterTheory.description}</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Content Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="grid w-full grid-cols-2 bg-white shadow-md p-1 rounded-lg relative z-20">
              <TabsTrigger
                value="theory"
                className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-md"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                Teorie
              </TabsTrigger>
              <TabsTrigger
                value="video"
                className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-md"
              >
                <Video className="w-4 h-4 mr-2" />
                Video
                {!chapterTheory.has_video_access && (
                  <Crown className="w-3 h-3 ml-1 text-yellow-500" />
                )}
              </TabsTrigger>
            </TabsList>

            {/* Theory Tab */}
            <TabsContent value="theory">
              <Card className="border-0 shadow-lg">
                <CardContent className="p-4 md:p-6">
                  {theoryLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : (
                    <div className="relative">
                      {/* Theory Content - with fade effect for preview */}
                      <div
                        className={`prose prose-slate max-w-none w-full theory-content ${chapterTheory.is_preview ? 'pb-8' : ''}`}
                        style={chapterTheory.is_preview ? {
                          maxHeight: '350px',
                          overflow: 'hidden',
                          maskImage: 'linear-gradient(to bottom, black 60%, transparent 100%)',
                          WebkitMaskImage: 'linear-gradient(to bottom, black 60%, transparent 100%)'
                        } : {}}
                        dangerouslySetInnerHTML={{ __html: chapterTheory.theory_content || '<p>Continutul teoriei nu este disponibil.</p>' }}
                      />

                      {/* Sneak Peek Upgrade Box - below the faded content */}
                      {chapterTheory.is_preview && (
                        <div className="mt-4 text-center p-6 bg-slate-50 rounded-xl border-2 border-blue-100">
                          <Lock className="w-10 h-10 text-blue-600 mx-auto mb-3" />
                          <h3 className="text-lg font-bold text-slate-900 mb-2">
                            Continut Complet Disponibil cu Abonament
                          </h3>
                          <p className="text-slate-600 text-sm mb-4">
                            Achizitioneaza pachetul Standard sau Premium pentru a accesa toata teoria.
                          </p>
                          <Button
                            onClick={() => navigate('/abonamente')}
                            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                          >
                            Vezi Pachetele
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </div>
                      )}

                      {/* Mark as complete button for users with access */}
                      {!chapterTheory.is_preview && user && (
                        <div className="mt-6 pt-4 border-t flex justify-end">
                          <Button
                            onClick={() => markTheoryComplete(selectedChapter.id)}
                            variant="outline"
                            className="border-green-500 text-green-600 hover:bg-green-50"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Am citit teoria
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Video Tab */}
            <TabsContent value="video">
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  {!chapterTheory.has_video_access ? (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Crown className="w-10 h-10 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2">
                        Videoclipurile sunt disponibile doar pentru Premium
                      </h3>
                      <p className="text-slate-600 mb-6 max-w-md mx-auto">
                        Upgrade la pachetul Premium pentru a accesa toate videoclipurile explicative.
                      </p>
                      <Button
                        onClick={() => navigate('/abonamente')}
                        className="bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600"
                      >
                        <Crown className="w-4 h-4 mr-2" />
                        Obtine Premium
                      </Button>
                    </div>
                  ) : chapterTheory.videos && chapterTheory.videos.length > 0 ? (
                    <div className="space-y-4">
                      {selectedVideo ? (
                        <div>
                          <Button variant="ghost" onClick={() => setSelectedVideo(null)} className="mb-4">
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Inapoi la lista
                          </Button>

                          {/* SECURED Video Player with Watermark & Protection */}
                          <div
                            className="relative aspect-video bg-black rounded-lg overflow-hidden mb-4 group"
                            onContextMenu={(e) => e.preventDefault()}
                            style={{ userSelect: 'none' }}
                          >
                            {/* YouTube iframe with security params */}
                            <iframe
                              src={`https://www.youtube.com/embed/${selectedVideo.youtube_id}?rel=0&modestbranding=1&showinfo=0&fs=1`}
                              title={selectedVideo.title}
                              className="w-full h-full"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />

                            {/* WATERMARK - Stânga sus */}
                            <div
                              className="absolute top-3 left-3 z-30 pointer-events-none"
                              style={{ userSelect: 'none' }}
                            >
                              <div className="bg-black/70 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-white/10">
                                <p className="text-white/90 text-sm font-semibold tracking-wide">
                                  Auto-Cora.ro
                                </p>
                                <p className="text-white/70 text-xs">
                                  Elev: {user?.name || 'Utilizator'}
                                </p>
                              </div>
                            </div>

                            {/* WATERMARK - Dreapta jos (semi-transparent) */}
                            <div
                              className="absolute bottom-14 right-3 z-30 pointer-events-none"
                              style={{ userSelect: 'none' }}
                            >
                              <p className="text-white/25 text-base font-bold tracking-widest">
                                AutoCora.ro • {user?.name || 'Utilizator'}
                              </p>
                            </div>

                            {/* BLOCKER TOP - Watch later, Share, Info (dreapta sus) */}
                            <div
                              className="absolute top-0 right-0 w-56 h-14 z-20 bg-slate-900/90 flex items-center justify-center"
                              onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
                              onContextMenu={(e) => e.preventDefault()}
                            >
                              <span className="text-white/50 text-xs">Auto-Cora.ro</span>
                            </div>

                            {/* Blocker pentru titlul video YouTube (stânga sus) */}
                            <div
                              className="absolute top-0 left-0 right-56 h-14 z-20 bg-slate-900/90"
                              onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
                              onContextMenu={(e) => e.preventDefault()}
                            />

                            {/* BLOCKER BOTTOM - MORE VIDEOS, Watch on YouTube, Logo */}
                            <div
                              className="absolute bottom-0 left-0 right-0 h-12 z-20 bg-slate-900/95 flex items-center justify-between px-4"
                              onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
                              onContextMenu={(e) => e.preventDefault()}
                            >
                              <span className="text-white/40 text-xs">Conținut protejat</span>
                              <span className="text-white/40 text-xs">© Auto-Cora.ro</span>
                            </div>

                            {/* Protected notice on hover */}
                            <div
                              className="absolute bottom-14 left-1/2 -translate-x-1/2 z-30 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <div className="bg-black/80 px-2 py-0.5 rounded text-[10px] text-white/50">
                                Conținut protejat © Auto-Cora.ro
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-bold text-lg">{selectedVideo.title}</h3>
                              <p className="text-slate-500 text-sm">{selectedVideo.duration}</p>
                            </div>
                            <Button
                              onClick={() => markVideoComplete(selectedChapter.id, selectedVideo.id)}
                              variant="outline"
                              className="border-green-500 text-green-600 hover:bg-green-50"
                            >
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Marcheaza ca vizionat
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="grid gap-3">
                          {chapterTheory.videos.map((video, idx) => (
                            <div
                              key={video.id}
                              onClick={() => setSelectedVideo(video)}
                              className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                            >
                              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                                <Play className="w-6 h-6 text-blue-600" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-medium text-slate-900">{video.title}</h4>
                                <p className="text-sm text-slate-500">{video.duration}</p>
                              </div>
                              {video.watched && (
                                <CheckCircle className="w-5 h-5 text-green-500" />
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-slate-500">
                      <Video className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Nu exista videoclipuri pentru acest capitol inca.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
          {/* Navigation între capitole */}
          {/* Navigation între capitole */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
            {previousChapter ? (
              <button
                onClick={() => navigate(`/cursuri/${previousChapter.id}`)}
                className="text-left p-5 rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-md hover:border-slate-300 transition group"
              >
                <div className="text-sm font-medium text-slate-500 mb-2">← Capitol anterior</div>
                <div className="text-lg font-semibold text-slate-900 group-hover:text-blue-600 transition">
                  {previousChapter.title}
                </div>
              </button>
            ) : (
              <div />
            )}

            {nextChapter ? (
              <button
                onClick={() => navigate(`/cursuri/${nextChapter.id}`)}
                className="text-left p-5 rounded-2xl border border-blue-200 bg-blue-50 shadow-sm hover:shadow-md hover:border-blue-300 transition group md:ml-auto"
              >
                <div className="text-sm font-medium text-blue-600 mb-2">Capitol următor →</div>
                <div className="text-lg font-semibold text-slate-900 group-hover:text-blue-700 transition">
                  {nextChapter.title}
                </div>
              </button>
            ) : (
              <div />
            )}
          </div>
        </div>
      </div>
    );
  }

  // Chapters List View
  return (
    <div className="min-h-screen bg-slate-100 py-8" data-testid="courses-page">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link to="/" className="text-blue-600 hover:text-blue-700 flex items-center gap-1 mb-2">
              <Home className="w-4 h-4" />
              <span className="text-sm">Acasa</span>
            </Link>
            <h1 className="text-3xl font-bold text-slate-900">Curs de Legislatie Rutiera</h1>
            <p className="text-slate-600 mt-1">18 capitole cu teorie, videoclipuri si chestionare</p>
          </div>
          {getUserPlanBadge()}
        </div>

        {/* Access Info Banner */}
        {!user && (
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 rounded-xl mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Eye className="w-8 h-8" />
              <div>
                <p className="font-semibold">Previzualizare gratuita disponibila!</p>
                <p className="text-blue-100 text-sm">Creeaza cont pentru a accesa continutul complet</p>
              </div>
            </div>
            <Button variant="secondary" onClick={() => navigate('/auth')}>
              Incepe Gratuit
            </Button>
          </div>
        )}

        {user && user.subscription_status !== "active" && (
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white p-4 rounded-xl mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Star className="w-8 h-8" />
              <div>
                <p className="font-semibold">Deblocheaza toate lectiile!</p>
                <p className="text-amber-100 text-sm">Standard: Teorie | Premium: Teorie + Video</p>
              </div>
            </div>
            <Button variant="secondary" onClick={() => navigate('/abonamente')}>
              Vezi Pachetele
            </Button>
          </div>
        )}

        {/* Progress Overview for logged in users with subscription */}
        {user && user.subscription_status === "active" && progressOverview && (
          <Card className="border-0 shadow-lg mb-6 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-4 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                    <Trophy className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">Progresul Tau</h3>
                    <p className="text-blue-100 text-sm">Continua sa inveti pentru a-ti lua permisul!</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-4xl font-bold">{progressOverview.overall_percent}%</div>
                  <p className="text-blue-100 text-sm">Completat</p>
                </div>
              </div>
            </div>
            <CardContent className="p-4">
              <div className="grid grid-cols-3 gap-4">
                {/* Theory Progress */}
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <BookOpen className="w-6 h-6 mx-auto mb-2 text-green-600" />
                  <div className="text-2xl font-bold text-slate-900">{progressOverview.theory.completed}/{progressOverview.theory.total}</div>
                  <p className="text-xs text-slate-500">Capitole Citite</p>
                  <Progress value={progressOverview.theory.percent} className="h-1.5 mt-2" />
                </div>
                {/* Videos Progress */}
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <Video className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                  <div className="text-2xl font-bold text-slate-900">{progressOverview.videos.watched}/{progressOverview.videos.total}</div>
                  <p className="text-xs text-slate-500">Video-uri Vazute</p>
                  <Progress value={progressOverview.videos.percent} className="h-1.5 mt-2" />
                </div>
                {/* Quizzes Progress */}
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <Target className="w-6 h-6 mx-auto mb-2 text-purple-600" />
                  <div className="text-2xl font-bold text-slate-900">{progressOverview.quizzes.passed}/{progressOverview.quizzes.total}</div>
                  <p className="text-xs text-slate-500">Chestionare Trecute</p>
                  <Progress value={progressOverview.quizzes.percent} className="h-1.5 mt-2" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Chapters Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {chapters.map((chapter) => (
              <Card
                key={chapter.id}
                className="border-0 shadow-md hover:shadow-xl transition-all cursor-pointer group overflow-hidden"
                onClick={() => handleSelectChapter(chapter)}
                data-testid={`chapter-card-${chapter.id}`}
              >
                <div className="relative">
                  {chapter.image_url && (
                    <div className="h-32 overflow-hidden">
                      <img
                        src={chapter.image_url}
                        alt={chapter.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    </div>
                  )}
                  <Badge className="absolute top-2 left-2 bg-blue-600 text-white">
                    Capitol {chapter.order}
                  </Badge>
                  {/* Completion badge */}
                  {chapter.chapter_complete && (
                    <div className="absolute top-2 right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                  )}
                </div>

                <CardContent className="p-4">
                  <h3 className="font-bold text-lg text-slate-900 mb-1 group-hover:text-blue-600 transition-colors">
                    {chapter.title}
                  </h3>
                  <p className="text-slate-600 text-sm mb-3 line-clamp-2">
                    {chapter.description}
                  </p>

                  {/* Content Indicators with completion status */}
                  <div className="flex items-center gap-2 text-xs flex-wrap">
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-full ${chapter.theory_completed ? 'bg-green-100 text-green-700' :
                      chapter.has_theory ? 'bg-slate-100 text-slate-600' : 'bg-slate-100 text-slate-400'
                      }`}>
                      <BookOpen className="w-3 h-3" />
                      <span>Teorie</span>
                      {chapter.theory_completed && <Check className="w-3 h-3" />}
                    </div>
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-full ${chapter.videos_watched >= chapter.videos_count && chapter.videos_count > 0 ? 'bg-green-100 text-green-700' :
                      chapter.videos_count > 0 ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-400'
                      }`}>
                      <Video className="w-3 h-3" />
                      <span>{chapter.videos_watched || 0}/{chapter.videos_count || 0}</span>
                      {chapter.videos_watched >= chapter.videos_count && chapter.videos_count > 0 && <Check className="w-3 h-3" />}
                    </div>
                  </div>

                  {/* Overall chapter progress bar */}
                  {user && (
                    <div className="mt-3">
                      {(() => {
                        const theoryDone = chapter.theory_completed ? 1 : 0;
                        const videosDone = chapter.videos_count > 0 ? (chapter.videos_watched || 0) / chapter.videos_count : 1;
                        const progress = Math.round(((theoryDone * 0.5) + (videosDone * 0.5)) * 100);
                        return (
                          <>
                            <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                              <span>Progres capitol</span>
                              <span>{progress}%</span>
                            </div>
                            <Progress value={progress} className="h-1.5" />
                          </>
                        );
                      })()}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}